<template>
  <div class="physics-nav">
    <div class="physics-nav-head">
      <span class="title">仿真资源</span>
      <button v-if="!physics" type="button" class="btn-mini" @click="onEnsure">初始化配置</button>
      <span v-else class="pn-ready" title="当前场景已创建仿真配置，可直接使用下方按钮添加资源">已就绪</span>
    </div>
    <p v-if="!physics" class="hint">尚未创建仿真配置。点击「初始化配置」后可添加区域、管段、源项与绑定。</p>
    <template v-else>
      <div class="pn-actions">
        <button type="button" class="btn-mini" @click="addZone">+ 区域</button>
        <button type="button" class="btn-mini" @click="addEdge">+ 管段</button>
        <button type="button" class="btn-mini" @click="addSource">+ 源项</button>
        <button type="button" class="btn-mini" @click="addBinding">+ 绑定</button>
      </div>
      <p class="hint pn-hint">点击条目在下方编辑详情（与 3D 选中互斥）。</p>
      <div class="section">
        <div class="section-title">区域 ({{ physics.zones.length }})</div>
        <ul class="item-list">
          <li
            v-for="z in physics.zones"
            :key="z.id"
            :class="{ active: isSel('zone', z.id) }"
            @click="pick('zone', z.id)"
          >
            {{ z.name || z.id }}
          </li>
          <li v-if="!physics.zones.length" class="empty">暂无</li>
        </ul>
      </div>
      <div class="section">
        <div class="section-title">管段 ({{ physics.flowNetwork.edges.length }})</div>
        <ul class="item-list">
          <li
            v-for="(e, ei) in physics.flowNetwork.edges"
            :key="e.id"
            :class="{ active: isSel('edge', e.id) }"
            @click="pick('edge', e.id)"
          >
            {{ edgeLineTitle(e, ei) }}
          </li>
          <li v-if="!physics.flowNetwork.edges.length" class="empty">暂无</li>
        </ul>
      </div>
      <div class="section">
        <div class="section-title">热源/源项 ({{ physics.sources.length }})</div>
        <ul class="item-list">
          <li
            v-for="s in physics.sources"
            :key="s.id"
            :class="{ active: isSel('source', s.id) }"
            @click="pick('source', s.id)"
          >
            {{ s.name }} · {{ physicsSourceTypeLabel(s.type) }}
          </li>
          <li v-if="!physics.sources.length" class="empty">暂无</li>
        </ul>
      </div>
      <div class="section">
        <div class="section-title">绑定 ({{ physics.bindings.length }})</div>
        <ul class="item-list">
          <li
            v-for="b in physics.bindings"
            :key="b.id"
            :class="{ active: isSel('binding', b.id) }"
            @click="pick('binding', b.id)"
          >
            {{ bindingLineText(b) }}
          </li>
          <li v-if="!physics.bindings.length" class="empty">暂无</li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useSceneStore, useSelectionStore } from '@/stores/scene'
import type { PhysicsBinding, PhysicsEditKind, PhysicsFlowEdge } from '@/types/scene'
import { flowMediumLabel, physicsSourceTypeLabel, physicsTargetKindLabel } from '@/utils/physicsUiLabels'
import { baseDisplayName, buildObjectDisplayNameMap } from '@/utils/objectDisplayNames'

const props = defineProps<{ onHistoryPush: () => void }>()

const sceneStore = useSceneStore()
const selectionStore = useSelectionStore()
const { physicsEditSelection } = storeToRefs(sceneStore)

const physics = computed(() => sceneStore.activeScene()?.physicsConfig)

const objectDisplayNameMap = computed(() => buildObjectDisplayNameMap(sceneStore.activeObjects()))

function objectLineLabel(id: string): string {
  const tid = id?.trim()
  if (!tid) return '（未选对象）'
  const objs = sceneStore.activeObjects()
  const o = objs.find((x) => x.id === tid)
  if (!o) return tid
  return objectDisplayNameMap.value.get(tid) ?? baseDisplayName(o)
}

function edgeLineTitle(e: PhysicsFlowEdge, index: number): string {
  const title = e.name?.trim() || `管段 ${index + 1}`
  return `${title} · ${flowMediumLabel(e.medium)}`
}

function bindingLineText(b: PhysicsBinding): string {
  const src = objectLineLabel(b.sourceObjectId)
  const kind = physicsTargetKindLabel(b.targetType)
  const tgt = bindingTargetTitle(b.targetType, b.targetId)
  return `${src} → ${kind}「${tgt}」｜源「${b.sourceMetric}」→「${b.targetMetric}」`
}

function bindingTargetTitle(kind: string, targetId: string): string {
  const p = physics.value
  if (!p || !targetId) return targetId || '—'
  if (kind === 'zone') {
    const z = p.zones.find((x) => x.id === targetId)
    return z ? z.name : targetId
  }
  if (kind === 'edge') {
    const edges = p.flowNetwork.edges
    const idx = edges.findIndex((x) => x.id === targetId)
    const e = idx >= 0 ? edges[idx] : undefined
    if (!e) return targetId
    return e.name?.trim() || `管段 ${idx + 1}`
  }
  if (kind === 'source') {
    const s = p.sources.find((x) => x.id === targetId)
    return s ? s.name : targetId
  }
  return targetId
}

function onEnsure() {
  const had = !!sceneStore.activeScene()?.physicsConfig
  sceneStore.ensurePhysicsConfig()
  if (!had) props.onHistoryPush()
}

function isSel(kind: PhysicsEditKind, id: string) {
  const s = physicsEditSelection.value
  return s?.kind === kind && s.id === id
}

function pick(kind: PhysicsEditKind, id: string) {
  selectionStore.deselect()
  sceneStore.setPhysicsEditSelection({ kind, id })
}

function addZone() {
  props.onHistoryPush()
  sceneStore.addPhysicsZone()
}

function addEdge() {
  props.onHistoryPush()
  sceneStore.addPhysicsFlowEdge()
}

function addSource() {
  props.onHistoryPush()
  sceneStore.addPhysicsSource()
}

function addBinding() {
  props.onHistoryPush()
  sceneStore.addPhysicsBinding()
}
</script>

<style scoped>
.physics-nav {
  width: 100%;
  flex: 0 1 auto;
  max-height: min(42vh, 360px);
  min-height: 0;
  overflow-y: auto;
  padding: 8px;
  font-size: 12px;
  color: #b8cce6;
  background: rgba(15, 23, 42, 0.95);
}
.physics-nav-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.title {
  font-weight: 600;
  color: #e2eeff;
}
.pn-ready {
  font-size: 10px;
  color: #5a7394;
  user-select: none;
  flex-shrink: 0;
}
.btn-mini {
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid #3d5575;
  background: #1a2838;
  color: #9ec5f0;
  cursor: pointer;
}
.pn-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}
.pn-hint {
  margin-top: 0;
  margin-bottom: 8px;
  font-size: 10px;
}
.hint {
  color: #7a93b5;
  line-height: 1.4;
}
.section {
  margin-top: 10px;
}
.section-title {
  font-size: 11px;
  color: #7dd3fc;
  margin-bottom: 4px;
}
.item-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.item-list li {
  padding: 4px 6px;
  border-radius: 4px;
  background: rgba(30, 42, 58, 0.6);
  margin-bottom: 4px;
  cursor: pointer;
  border: 1px solid transparent;
}
.item-list li:hover {
  border-color: #3d5575;
}
.item-list li.active {
  border-color: #3b82f6;
  background: rgba(30, 58, 95, 0.5);
}
.item-list li.empty {
  background: transparent;
  color: #5a7394;
  cursor: default;
  border: none;
}
</style>
